<template>
  <header
    class="sticky top-0 z-50 header-box-shadow"
    role="banner"
    aria-label="Main site header"
  >
    <div
      class="header-main-container flex flex-col lg:flex-row justify-center items-center font-rubik"
    >
      <div
        class="header-content-wrapper w-full h-full flex flex-row justify-center items-center"
      >
        <!-- DESKTOP -->
        <nav
          class="max-w-1440 w-full h-full hidden px-6 lg:flex flex-col gap-2"
          aria-label="Primary navigation"
        >
          <!-- Top row -->
          <div
            class="w-full h-full hidden lg:flex flex-row justify-between items-center"
            style="min-height: 60px;"
          >
            <Logo />

            <!-- Dropdown buttons -->
            <DropdownButtons
              ref="dropdownButtons"
              @update:activeButton="handleActiveButtonChange"
            />

            <FindInventory class="find-inventory" />

            <!-- Phone & quote -->
            <div
              class="flex flex-row justify-between items-center gap-4"
              aria-label="Sales contact"
            >
              <a
                :href="`tel:${organizationPhone || phoneNumber}`"
                class="header-phone hidden lg:inline"
                :aria-label="`Call us at ${
                  formatPhoneNumber(organizationPhone || phoneNumber)
                }`"
              >
                {{ formatPhoneNumber(organizationPhone || phoneNumber) }}
              </a>
              <quote-summary-dropdown
                v-if="quote && !isMobile"
                key="desktop-quote"
              />
              <button
                v-else
                type="button"
                class="get-quote-button flex items-center justify-center"
                aria-label="Get a quote"
                data-vue-init="lead-flow"
                data-props='{ "gtag": "get_quote"}'
              >
                <p>Get Quote</p>
              </button>
            </div>
          </div>

          <!-- Second row (service‑area / partner links) -->
          <transition name="fade-slide">
            <div
              v-if="showFullHeader && showLastRowNavLinks"
              class="full-header-container w-full hidden lg:flex flex-col justify-center"
            >
              <NavigationRow
                :organization="organization"
                :solutions="solutions"
                :organizationPhone="organizationPhone"
                :phoneNumber="phoneNumber"
                :formatPhoneNumber="formatPhoneNumber"
                :city="city"
                :state="state"
                :solutionsPath="solutionsPath"
              />
            </div>
          </transition>

          <!-- Dropdown container -->
          <div
            v-if="activeDropdown"
            ref="desktopDropdown"
            :class="[
              'header-dropdown-container header-box-shadow w-full hidden lg:flex flex-col justify-center items-center',
              showFullHeader && showLastRowNavLinks
                ? 'header-dropdown-container-full-header-top'
                : 'header-dropdown-container-default-top'
            ]"
          >
            <DesktopDropdown
              :activeDropdown="activeDropdown"
              :dropdownLinks="dropdownLinks"
            />
          </div>
        </nav>

        <!-- MOBILE -->
        <nav
          class="lg:hidden w-full h-full px-4 flex flex-col"
          aria-label="Mobile navigation"
        >
          <!-- Mobile top row -->
          <div
            :class="[
              'w-full h-full flex flex-row justify-between items-center',
              showMobileBottomNav ? 'mobile-header-min-h' : ''
            ]"
          >
            <Logo />

            <!-- Quick actions -->
            <div
              v-if="!isActiveMobileMenu"
              class="flex flex-row justify-between items-center gap-2"
            >
              <quote-summary-dropdown
                v-if="quote && isMobile"
                key="mobile-quote"
              />
              <MobileGetQuoteButton v-else />
              <MobilePhoneButton
                :organizationPhone="organizationPhone"
                :phoneNumber="phoneNumber"
              />
              <MobileHamburgerButton
                @update:activeMobileMenu="handleActiveMobileMenuChange"
              />
            </div>

            <!-- Close button -->
            <MobileCloseButton
              v-if="isActiveMobileMenu"
              @update:activeMobileMenu="handleActiveMobileMenuChange"
            />
          </div>

          <!-- Partner / location row -->
          <MobilePartnerLocationRow
            v-if="showMobileBottomNav && showFullHeader && !isActiveMobileMenu"
            :organization="organization"
            :city="city"
            :state="state"
          />
        </nav>
      </div>
    </div>

    <!-- Mobile side menu -->
    <MobileSideMenu
      class="lg:hidden"
      v-if="isActiveMobileMenu"
      :dropdownLinks="dropdownLinks"
      @close="handleActiveMobileMenuChange(false)"
    />

    <!-- Sticky bottom nav on mobile -->
    <div
      class="lg:hidden bottom-nav"
      v-if="!isActiveMobileMenu && showFullHeader && showMobileBottomNav"
    >
      <MobileBottomNav
        :organization="organization"
        :solutionsPath="solutionsPath"
        :solutions="solutions"
      />
    </div>
  </header>
</template>

<script>
import { mapState } from 'vuex'
import QuoteSummaryDropdown from '../quote/summary/dropdown.vue'
import { formatPhoneNumber } from '../../shared/utils'

import DropdownButtons from './helpers/dropdown-buttons.vue'
import FindInventory from './helpers/find-inventory.vue'
import Logo from './helpers/logo.vue'
import MobileGetQuoteButton from './helpers/mobile-get-quote-button.vue'
import MobilePhoneButton from './helpers/mobile-phone-button.vue'
import MobileHamburgerButton from './helpers/mobile-hamburger-button.vue'
import MobileCloseButton from './helpers/mobile-close-button.vue'
import DesktopDropdown from './helpers/desktop-dropdown/index.vue'
import MobileSideMenu from './helpers/mobile-side-menu/index.vue'
import NavigationRow from './helpers/navigationRow.vue'
import MobileBottomNav from './helpers/mobile-bottom-nav.vue'
import MobilePartnerLocationRow from './helpers/mobile-partner-location-row.vue'

export default {
  components: {
    DropdownButtons,
    FindInventory,
    QuoteSummaryDropdown,
    Logo,
    MobileGetQuoteButton,
    MobilePhoneButton,
    MobileHamburgerButton,
    MobileCloseButton,
    DesktopDropdown,
    MobileSideMenu,
    NavigationRow,
    MobileBottomNav,
    MobilePartnerLocationRow
  },
  props: {
    organizationPhone: String,
    organization: {
      required: true,
      type: Object
    },
    solutionsPath: {
      type: Object
    },
    solutions: {
      type: Array,
      required: true
    },
    city: String,
    state: String,
    showFullHeader: Boolean
  },
  data() {
    return {
      isActiveMobileMenu: false,
      activeDropdown: '',
      scrollPosition: 0,
      isMobileViewport: window.innerWidth < 1024,
      dropdownLinks: {
        solutions: {
          name: 'Solutions',
          links: [
            { title: 'Container Sales', slug: '/solutions/container-sales' },
            { title: 'Container Rentals', slug: '/solutions/container-rentals' },
            { title: 'Container Modifications', slug: '/solutions/container-modifications' },
            { title: 'Refrigerated Containers', slug: '/solutions/refrigerated-containers' },
            { title: 'Portable Offices', slug: '/solutions/portable-offices' }
          ]
        },
        guides: {
          name: 'Guides',
          links: [
            { title: 'Container Buyers Guide', slug: '/buyers-guide' },
            { title: 'Container Rental Guide', slug: '/renters-guide' },
            { title: 'Refrigerated Buyers Guide', slug: '/refrigerated-containers-buyers-guide' },
            { title: 'Refrigerated Rental Guide', slug: '/refrigerated-containers-renters-guide' },
            { title: 'Container Sizes Guide', slug: '/shipping-container-specifications' },
            { title: 'Delivery Information', slug: '/delivery-information' }
          ],
          seeAll: { slug: '/informative-articles' }
        },
        resources: {
          name: 'Resources',
          links: [
            { title: 'Container Sales', slug: '/containers-for-sale' },
            { title: 'Container Modifications', slug: '/container-modifications' },
            { title: 'Container Conditions', slug: '/container-conditions' },
            { title: 'Container Specifications', slug: '/shipping-container-specifications' },
            { title: 'Container Modifications', slug: '/container-modifications' },
            { title: 'Delivery Information', slug: '/delivery-information' },
            { title: 'Container Conditions', slug: '/container-conditions' }
          ],
          seeAll: { slug: '/informative-articles' }
        },
        company: {
          name: 'Company',
          links: [
            { title: 'About Us', slug: '/about' },
            { title: 'Our Services', slug: '/service-areas' },
            { title: 'Contact Us', slug: '/contact' },
            { title: 'Partner Program', slug: '/partner-program' },
            { title: 'Privacy Policy', slug: '/privacy-policy' },
            { title: 'Return Policy', slug: '/return-policy' },
            { title: 'Terms of Use', slug: '/terms-of-use' }            
          ]
        }
      }
    }
  },
  computed: {
    ...mapState('shared', ['phoneNumber', 'wizardEnabled']),
    ...mapState('quote', ['quote']),
    showLastRowNavLinks() {
      return this.scrollPosition > 680
    },
    showMobileBottomNav() {
      return this.scrollPosition > 590 
    },
    isMobile() {
      return this.isMobileViewport
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    formatPhoneNumber,
    handleActiveButtonChange(activeDropdown) {
      this.activeDropdown = activeDropdown
    },
    handleActiveMobileMenuChange(action) {
      this.isActiveMobileMenu = action
    },
    handleScroll() {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop
    },
    handleResize() {
      this.isMobileViewport = window.innerWidth < 1024
    },
    handleOutsideClick(e) {
      if (!this.activeDropdown) return
      const dropdown = this.$refs.desktopDropdown
      const trigger  = this.$refs.dropdownButtons && this.$refs.dropdownButtons.$el
      if (
        dropdown &&
        !dropdown.contains(e.target) &&
        (!trigger || !trigger.contains(e.target))
      ) {
        this.activeDropdown = ''
      }
    }
  }
}
</script>

<style scoped>
.header-box-shadow {
  box-shadow: 0px 79px 22px 0px rgba(0, 0, 0, 0.00), 0px 50px 20px 0px rgba(0, 0, 0, 0.01), 0px 28px 17px 0px rgba(0, 0, 0, 0.03), 0px 13px 13px 0px rgba(0, 0, 0, 0.04), 0px 3px 7px 0px rgba(0, 0, 0, 0.05);
}

.header-main-container {
  position: sticky;
  top: 0;
  z-index: 50;
  background-color: white;
  min-height: 60px;
}

.header-content-wrapper {
  position: relative;
}

.header-dropdown-container {
  border-top: 1px solid #EDE9E3;
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  height: 308px;
  z-index: 100;
  background: white;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

/* Transition for the last row nav links */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.1s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.header-dropdown-container-default-top {
  top: 60px;
}

.header-dropdown-container-full-header-top {
  top: 104px;
}

.find-inventory {
  width: 448px;
}

.get-quote-button {
  width: 121px;
  height: 44px;
  background-color: #3565FC;
  border: 1px solid #90B6FF;
  border-radius: 99px;
}

.get-quote-button p {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.04em;
  color: #FFF;
}

.header-phone,
.subheader-phone {
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: -0.0125em;
  color: #334155;
}

.full-header-container {
  height: 44px;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 52px;
  border: 1px solid red;
  background-color: white;
  z-index: 100;
}

.mobile-header-min-h {
  height: 60px;
}
</style>
